'use client';

import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ── Types ─────────────────────────────────────────────────────────────── */

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqCategory {
  id: string;
  label: string;
  items: FaqItem[];
}

interface FaqAccordionProps {
  categories: FaqCategory[];
}

/* ── Helpers ───────────────────────────────────────────────────────────── */

function normalise(text: string): string {
  return text.toLowerCase().replace(/\s+/g, ' ').trim();
}

/* ── Component ─────────────────────────────────────────────────────────── */

export default function FaqAccordion({ categories }: FaqAccordionProps) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  /* Build a flat list with category id attached so we can filter globally. */
  const allItems = useMemo(() => {
    const items: (FaqItem & { catId: string; flatIndex: number })[] = [];
    let idx = 0;
    for (const cat of categories) {
      for (const item of cat.items) {
        items.push({ ...item, catId: cat.id, flatIndex: idx });
        idx++;
      }
    }
    return items;
  }, [categories]);

  /* Filter by search text + category. */
  const filtered = useMemo(() => {
    const q = normalise(search);
    return allItems.filter((item) => {
      const matchesCat = !activeCategory || item.catId === activeCategory;
      if (!matchesCat) return false;
      if (!q) return true;
      return (
        normalise(item.question).includes(q) ||
        normalise(item.answer).includes(q)
      );
    });
  }, [allItems, search, activeCategory]);

  /* Regroup into categories for display. */
  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; items: typeof filtered }>();
    for (const cat of categories) {
      const catItems = filtered.filter((i) => i.catId === cat.id);
      if (catItems.length > 0) map.set(cat.id, { label: cat.label, items: catItems });
    }
    return map;
  }, [filtered, categories]);

  const toggle = useCallback(
    (id: string) => setOpenId((prev) => (prev === id ? null : id)),
    [],
  );

  /* Reset open item when filters change. */
  useEffect(() => {
    setOpenId(null);
  }, [search, activeCategory]);

  return (
    <div>
      {/* Search input */}
      <div className="relative mb-6">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2"
          style={{ color: 'var(--pub-text-muted)' }}
          aria-hidden="true"
        />
        <input
          type="text"
          placeholder="Search questions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-12 w-full rounded-full border bg-[var(--pub-surface-card)] pl-11 pr-4 text-sm outline-none transition-colors placeholder:text-[var(--pub-text-muted)] focus:border-[var(--pub-ember)]"
          style={{
            borderColor: 'var(--pub-border-default)',
            color: 'var(--pub-text-primary)',
          }}
          aria-label="Search frequently asked questions"
        />
      </div>

      {/* Category filter pills */}
      <div className="mb-8 flex flex-wrap gap-2" role="tablist" aria-label="FAQ categories">
        <button
          type="button"
          role="tab"
          aria-selected={!activeCategory}
          onClick={() => setActiveCategory(null)}
          className={cn(
            'inline-flex h-9 items-center rounded-full px-4 text-sm font-medium transition-colors',
            !activeCategory
              ? 'text-white'
              : 'text-[var(--pub-text-secondary)] hover:text-[var(--pub-text-primary)]',
          )}
          style={{
            background: !activeCategory ? 'var(--pub-ember)' : 'var(--pub-surface-card)',
            border: `1px solid ${!activeCategory ? 'var(--pub-ember)' : 'var(--pub-border-subtle)'}`,
          }}
        >
          All
        </button>
        {categories.map((cat) => {
          const active = activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveCategory(active ? null : cat.id)}
              className={cn(
                'inline-flex h-9 items-center rounded-full px-4 text-sm font-medium transition-colors',
                active
                  ? 'text-white'
                  : 'text-[var(--pub-text-secondary)] hover:text-[var(--pub-text-primary)]',
              )}
              style={{
                background: active ? 'var(--pub-ember)' : 'var(--pub-surface-card)',
                border: `1px solid ${active ? 'var(--pub-ember)' : 'var(--pub-border-subtle)'}`,
              }}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Accordion list */}
      {grouped.size === 0 ? (
        <div
          className="rounded-[var(--pub-radius-xl)] p-8 text-center"
          style={{
            background: 'var(--pub-surface-card)',
            border: '1px solid var(--pub-border-subtle)',
          }}
        >
          <p style={{ color: 'var(--pub-text-secondary)', fontSize: '15px' }}>
            No questions match your search. Try different words or{' '}
            <a
              href="/contact"
              style={{ color: 'var(--pub-ember)', fontWeight: 600, textDecoration: 'underline' }}
            >
              contact support
            </a>
            .
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {[...grouped.entries()].map(([catId, { label, items }]) => (
            <section key={catId}>
              <h3
                className="mb-3 text-xs font-medium uppercase tracking-[0.1em]"
                style={{
                  color: 'var(--pub-text-muted)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                }}
              >
                {label}
              </h3>
              <div className="space-y-2">
                {items.map((item) => {
                  const itemId = `faq-${catId}-${item.flatIndex}`;
                  const isOpen = openId === itemId;
                  return (
                    <div
                      key={itemId}
                      className="overflow-hidden rounded-[var(--pub-radius-lg)] transition-colors"
                      style={{
                        background: 'var(--pub-surface-card)',
                        border: `1px solid ${isOpen ? 'var(--pub-ember)' : 'var(--pub-border-subtle)'}`,
                      }}
                    >
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={`${itemId}-answer`}
                        id={`${itemId}-question`}
                        onClick={() => toggle(itemId)}
                        className="flex w-full items-center gap-3 p-5 text-left transition-colors hover:bg-[var(--pub-surface-base)]/50"
                      >
                        <span
                          className="flex-1 text-sm font-semibold leading-snug"
                          style={{ color: 'var(--pub-text-primary)' }}
                        >
                          {item.question}
                        </span>
                        <ChevronDown
                          className="h-4 w-4 shrink-0 transition-transform duration-200"
                          style={{
                            color: 'var(--pub-text-muted)',
                            transform: isOpen ? 'rotate(180deg)' : undefined,
                          }}
                          aria-hidden="true"
                        />
                      </button>
                      <div
                        id={`${itemId}-answer`}
                        role="region"
                        aria-labelledby={`${itemId}-question`}
                        className="overflow-hidden transition-all duration-200"
                        style={{
                          maxHeight: isOpen ? '400px' : '0px',
                          opacity: isOpen ? 1 : 0,
                        }}
                      >
                        <p
                          className="px-5 pb-5 text-sm leading-relaxed"
                          style={{ color: 'var(--pub-text-secondary)' }}
                        >
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
